import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import WalletManagerMUI from "../../components/WalletManagerMUI";
import DexSelector from "../../components/DexSelector";
import NetworkSelector from "../../components/NetworkSelector";
import TokenPair from "../../components/TokenPair";
import BatchTaskSettings from "../../components/BatchTaskSettings";
import BscPriceMonitor from "../../components/BscPriceMonitor";
import { BscWebSocketProvider } from "../../contexts/BscWebSocketContext";
import { useBscWebSocket } from "../../contexts/BscWebSocketContext";
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Alert,
  LinearProgress,
} from "@mui/material";
import "./BscSwap.css";
import {
  AmountType,
  DexConfig,
  DexServiceConfig,
  LogData,
  LogLevel,
  QueueConfig,
  QueueRunMode,
  QueueStopCondition,
  RpcConfig,
  SwapParams,
  TaskConfig,
  TokenInfo,
  WalletConfig,
  CreateLogParams,
} from "../../types/dex";
import { useDexService } from "../../contexts/DexServiceContext";
import { useDexConfigRedux } from "../../hooks/useDexConfigRedux";
import { useDispatch } from "react-redux";
import { updateSwapConfig } from "../../store/dexConfigSlice";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  TextField,
  InputAdornment,
  Slider,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import SwapSettings from "../../components/SwapSettings";
import GasSettings from "../../components/GasSettings";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import SyncIcon from "@mui/icons-material/Sync";
import { useDexWorker } from "../../hooks/useDexWorker";
import { ethers } from "ethers";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

interface WorkerResponse {
  workerId: number;
  success: boolean;
  data?: any;
  error?: string;
}

const BscSwap: React.FC = () => {
  const { addLog, isInitialized, initialize } = useDexService();
  const { status, error, subscribe, unsubscribe, onPriceUpdate } =
    useBscWebSocket();
  // 使用Redux管理DEX配置
  const {
    dexConfig,
    wallets,
    rpc,
    dex,
    task,
    swapConfig,
    queueConfig,
    otherConfig,
    tokens,
    swapParams,
    gasSettings,
    setWallets,
    setRpc,
    setDex,
    setToken1,
    setToken2,
    setTask,
    updatePartialParams,
    initSwapParamsFromTokens,
    setAmountType,
    setMinAmount,
    setMaxAmount,
    setFixedAmount,
    setReserveAmount,
    // 重命名从useDexConfigRedux导入的setAutoSell函数，避免重复声明
    setAutoSell: updateAutoSell,
    setSellAmountType,
    setSellMinAmount,
    setSellMaxAmount,
    setSellFixedAmount,
    setSellReserveAmount,
  } = useDexConfigRedux();
  const dispatch = useAppDispatch();
  const [selectedWallets, setSelectedWallets] = useState<WalletConfig[]>([]);
  const [showWalletManager, setShowWalletManager] = useState<boolean>(true);

  // 钱包配置
  const [walletConfigs, setWalletConfigs] = useState<WalletConfig[]>([]);
  // 选中的钱包配置
  const [selectedWalletIndices, setSelectedWalletIndices] = useState<number[]>(
    []
  );

  // 使用本地状态管理钱包余额查询状态，不依赖Redux
  const [walletQueryStatus, setWalletQueryStatus] = useState<{
    [key: string]: {
      status?: "pending" | "success" | "error";
      errorMessage?: string;
      bnbBalance?: string;
      usdtBalance?: string;
      token1Balance?: string;
      token2Balance?: string;
      nonce?: number;
    };
  }>({});

  // 合并当前钱包配置和查询状态，用于展示
  const combinedWalletConfigs = useMemo(() => {
    return walletConfigs.map((wallet, index) => {
      const queryStatus = wallet.address
        ? walletQueryStatus[wallet.address]
        : undefined;
      return {
        ...wallet,
        status: queryStatus?.status,
        errorMessage: queryStatus?.errorMessage,
        bnbBalance:
          queryStatus?.bnbBalance !== undefined
            ? queryStatus.bnbBalance
            : wallet.bnbBalance,
        usdtBalance:
          queryStatus?.usdtBalance !== undefined
            ? queryStatus.usdtBalance
            : wallet.usdtBalance,
        token1Balance:
          queryStatus?.token1Balance !== undefined
            ? queryStatus.token1Balance
            : wallet.token1Balance,
        token2Balance:
          queryStatus?.token2Balance !== undefined
            ? queryStatus.token2Balance
            : wallet.token2Balance,
        nonce:
          queryStatus?.nonce !== undefined ? queryStatus.nonce : wallet.nonce,
      };
    });
  }, [walletConfigs, walletQueryStatus]);

  // 初始化时从Redux加载钱包配置
  useEffect(() => {
    if (wallets && wallets.length > 0) {
      setWalletConfigs(wallets);

      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: "已从全局状态加载钱包配置",
        data: {
          count: wallets.length,
          addresses: wallets.filter((w) => w.address).map((w) => w.address),
        },
      };
      addLog(log);
    }
  }, [wallets, addLog]);

  // 处理DEX选择变更
  const handleDexChange = useCallback(
    (newDex: DexConfig) => {
      // 更新Redux中的DEX配置
      console.log("更新DEX配置:", newDex);
      setDex(newDex);

      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: "DEX配置已更新",
        data: {
          router: newDex.router,
          factory: newDex.factory,
        },
      };
      addLog(log);
    },
    [addLog, setDex]
  );

  // 处理网络选择变更
  const handleNetworkChange = useCallback(
    (newRpc: RpcConfig) => {
      // 更新Redux中的RPC配置
      setRpc(newRpc);

      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: "网络配置已更新",
        data: {
          chainId: newRpc.chainId,
          url: newRpc.url,
        },
      };
      addLog(log);
    },
    [addLog, setRpc]
  );

  // 处理MUI版WalletManager钱包选择
  const handleMUIWalletSelect = useCallback(
    (wallets: WalletConfig[]) => {
      // 保存钱包索引而不是钱包对象
      const indices = wallets
        .map((wallet) => {
          const index = walletConfigs.findIndex(
            (w) => w.address === wallet.address
          );
          return index >= 0 ? index : -1;
        })
        .filter((index) => index >= 0);

      setSelectedWalletIndices(indices);
      // 同时更新选中的钱包列表
      setSelectedWallets(wallets);
    },
    [walletConfigs]
  );

  // 切换钱包管理器显示 - 使用useCallback避免不必要的函数重建
  const toggleWalletManager = useCallback(() => {
    setShowWalletManager(!showWalletManager);
  }, [showWalletManager]);

  // 处理钱包配置变更
  const handleWalletConfigChange = useCallback(
    (newWallets: WalletConfig[]) => {
      setWalletConfigs(newWallets);
      // 同步到Redux
      setWallets(newWallets);

      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: "钱包配置已更新",
        data: {
          count: newWallets.length,
          addresses: newWallets.filter((w) => w.address).map((w) => w.address),
        },
      };
      addLog(log);
    },
    [addLog, setWallets]
  );

  // 处理MUI版WalletManager钱包导入
  const handleMUIWalletImport = useCallback(
    (newWallets: WalletConfig[]) => {
      // 合并现有钱包和新导入的钱包
      const updatedWallets = [...walletConfigs, ...newWallets];

      handleWalletConfigChange(updatedWallets);
    },
    [walletConfigs, handleWalletConfigChange]
  );

  // 处理MUI版WalletManager钱包删除
  const handleMUIWalletDelete = useCallback(
    (indices: number[]) => {
      if (indices.length === 0) return;

      if (window.confirm(`确定要删除选中的 ${indices.length} 个钱包吗？`)) {
        // 按索引从大到小排序，以便从后向前删除，避免索引变化
        const sortedIndices = [...indices].sort((a, b) => b - a);

        // 创建新的钱包数组，移除要删除的钱包
        const updatedWallets = [...walletConfigs];
        sortedIndices.forEach((index) => {
          updatedWallets.splice(index, 1);
        });

        // 更新钱包配置
        handleWalletConfigChange(updatedWallets);

        // 清空选中状态
        setSelectedWalletIndices([]);
        setSelectedWallets([]);
      }
    },
    [walletConfigs, handleWalletConfigChange]
  );

  // 处理MUI版WalletManager清空所有钱包
  const handleMUIClearAll = useCallback(() => {
    if (walletConfigs.length === 0) return;

    if (
      window.confirm(
        `确定要清空所有 ${walletConfigs.length} 个钱包吗？此操作不可恢复！`
      )
    ) {
      handleWalletConfigChange([]);
      setSelectedWalletIndices([]);
    }
  }, [
    walletConfigs.length,
    handleWalletConfigChange,
    setSelectedWalletIndices,
  ]);

  // 处理MUI版WalletManager查询余额
  const handleMUICheckBalance = useCallback(
    async (indices: number[]) => {
      if (indices.length === 0) return;

      // 创建日志记录
      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: `开始查询 ${indices.length} 个钱包的余额和Nonce`,
        data: {
          count: indices.length,
          indices,
        },
      };
      addLog(log);

      try {
        // 获取当前钱包配置
        const currentWalletConfigs = [...walletConfigs];

        // 创建临时状态对象
        const newQueryStatus = { ...walletQueryStatus };

        // 标记选中的钱包为查询中状态
        indices.forEach((index) => {
          if (index >= 0 && index < currentWalletConfigs.length) {
            const wallet = currentWalletConfigs[index];
            if (wallet.address) {
              newQueryStatus[wallet.address] = {
                ...newQueryStatus[wallet.address],
                status: "pending",
                errorMessage: undefined,
              };
            }
          }
        });

        // 更新查询状态
        setWalletQueryStatus(newQueryStatus);

        // 创建Provider实例
        const provider = new ethers.providers.JsonRpcProvider(rpc.url);

        // 获取USDT合约地址（使用BSC上的USDT地址）
        const usdtAddress = "0x55d398326f99059fF775485246999027B3197955"; // BSC上的USDT地址

        // 创建USDT合约实例
        const usdtContract = new ethers.Contract(
          usdtAddress,
          [
            "function balanceOf(address) view returns (uint256)",
            "function decimals() view returns (uint8)",
          ],
          provider
        );

        // 获取USDT精度，使用try-catch包裹
        let usdtDecimals = 18; // 默认值
        try {
          usdtDecimals = await usdtContract.decimals();
        } catch (error) {
          console.warn("无法获取USDT精度，使用默认值18:", error);
        }

        // 获取Token1合约实例（如果已选择）
        let token1Contract: ethers.Contract | null = null;
        let token1Decimals = 18; // 默认精度
        let isValidToken1 = false;

        if (
          swapConfig?.token1 &&
          swapConfig.token1.address &&
          ethers.utils.isAddress(swapConfig.token1.address) &&
          swapConfig.token1.address !== usdtAddress
        ) {
          try {
            token1Contract = new ethers.Contract(
              swapConfig.token1.address,
              [
                "function balanceOf(address) view returns (uint256)",
                "function decimals() view returns (uint8)",
              ],
              provider
            );
            token1Decimals = await token1Contract.decimals();
            isValidToken1 = true;
          } catch (error) {
            console.warn("无法创建Token1合约实例:", error);
          }
        }

        // 获取Token2合约实例（如果已选择）
        let token2Contract: ethers.Contract | null = null;
        let token2Decimals = 18; // 默认精度
        let isValidToken2 = false;

        if (
          swapConfig?.token2 &&
          swapConfig.token2.address &&
          ethers.utils.isAddress(swapConfig.token2.address) &&
          swapConfig.token2.address !== usdtAddress
        ) {
          try {
            token2Contract = new ethers.Contract(
              swapConfig.token2.address,
              [
                "function balanceOf(address) view returns (uint256)",
                "function decimals() view returns (uint8)",
              ],
              provider
            );
            token2Decimals = await token2Contract.decimals();
            isValidToken2 = true;
          } catch (error) {
            console.warn("无法创建Token2合约实例:", error);
          }
        }

        // 定义每批处理的钱包数量
        const batchSize = 10;
        // 把索引分组，每组batchSize个
        const indexBatches: number[][] = [];
        for (let i = 0; i < indices.length; i += batchSize) {
          indexBatches.push(indices.slice(i, i + batchSize));
        }

        // 定义单个钱包查询函数
        interface WalletQueryResult {
          address: string;
          bnbBalance?: string;
          usdtBalance?: string;
          token1Balance?: string;
          token2Balance?: string;
          nonce?: number;
          status: "success" | "error" | "pending";
          errorMessage?: string;
        }
        
        const queryWallet = async (index: number): Promise<WalletQueryResult | undefined> => {
          if (index < 0 || index >= walletConfigs.length) return undefined;
          
          const wallet = walletConfigs[index];
          if (!wallet.address) return undefined;

          try {
            // 获取BNB余额
            const bnbBalance = await provider.getBalance(wallet.address);
            const bnbBalanceStr = ethers.utils.formatEther(bnbBalance);

            // 获取USDT余额
            let usdtBalance = ethers.BigNumber.from(0);
            try {
              usdtBalance = await usdtContract.balanceOf(wallet.address);
            } catch (error) {
              console.warn(`获取钱包[${index}] USDT余额失败:`, error);
            }
            const usdtBalanceStr = ethers.utils.formatUnits(
              usdtBalance,
              usdtDecimals
            );

            // 获取Token1余额（如果有效）
            let token1Balance = ethers.BigNumber.from(0);
            let token1BalanceStr = "0";
            if (isValidToken1 && token1Contract) {
              try {
                token1Balance = await token1Contract.balanceOf(wallet.address);
                token1BalanceStr = ethers.utils.formatUnits(
                  token1Balance,
                  token1Decimals
                );
              } catch (error) {
                console.warn(`获取钱包[${index}] Token1余额失败:`, error);
              }
            }

            // 获取Token2余额（如果有效）
            let token2Balance = ethers.BigNumber.from(0);
            let token2BalanceStr = "0";
            if (isValidToken2 && token2Contract) {
              try {
                token2Balance = await token2Contract.balanceOf(wallet.address);
                token2BalanceStr = ethers.utils.formatUnits(
                  token2Balance,
                  token2Decimals
                );
              } catch (error) {
                console.warn(`获取钱包[${index}] Token2余额失败:`, error);
              }
            }

            // 获取Nonce
            const nonce = await provider.getTransactionCount(wallet.address);

            console.log(`钱包[${index}] ${wallet.address} 余额查询成功:`, {
              BNB: bnbBalanceStr,
              USDT: usdtBalanceStr,
              [swapConfig?.token1?.symbol || "Token1"]: token1BalanceStr,
              [swapConfig?.token2?.symbol || "Token2"]: token2BalanceStr,
              nonce,
            });

            // 返回查询结果
            return {
              address: wallet.address,
              bnbBalance: bnbBalanceStr,
              usdtBalance: usdtBalanceStr,
              token1Balance: token1BalanceStr,
              token2Balance: token2BalanceStr,
              nonce,
              status: "success"
            };
          } catch (error) {
            console.error(`钱包[${index}] 余额查询失败:`, error);
            // 返回错误信息
            return {
              address: wallet.address,
              status: "error",
              errorMessage: (error as Error).message || "余额查询失败"
            };
          }
        };
        
        // 分批执行查询
        let processedCount = 0;
        for (const batch of indexBatches) {
          console.log(`开始处理第 ${processedCount/batchSize + 1} 批钱包，共 ${batch.length} 个`);
          
          // 并行查询当前批次的所有钱包
          const batchResults = await Promise.all(batch.map(index => queryWallet(index)));
          
          // 更新查询状态
          const updatedQueryStatus = { ...newQueryStatus };
          batchResults.forEach(result => {
            if (result && result.address) {
              updatedQueryStatus[result.address] = result;
            }
          });
          
          setWalletQueryStatus(updatedQueryStatus);
          processedCount += batch.length;
          
          console.log(`已完成 ${processedCount}/${indices.length} 个钱包的查询`);
          
          // 更新临时状态对象
          Object.assign(newQueryStatus, updatedQueryStatus);
        }

        // 创建日志记录
        const successLog: CreateLogParams = {
          level: LogLevel.INFO,
          message: `查询 ${indices.length} 个钱包余额完成`,
          data: null,
        };
        addLog(successLog);
      } catch (error) {
        console.error("余额查询出错:", error);

        // 创建日志记录
        const errorLog: CreateLogParams = {
          level: LogLevel.ERROR,
          message: `钱包余额查询失败: ${(error as Error).message}`,
          data: { error },
        };
        addLog(errorLog);
      }
    },
    [walletConfigs, addLog, rpc.url, swapConfig, walletQueryStatus]
  );

  // 处理代币1选择
  const handleToken1Change = useCallback(
    (token: TokenInfo) => {
      console.log("选择代币1:", token);
      setToken1(token);
    },
    [setToken1]
  );

  // 处理代币2选择
  const handleToken2Change = useCallback(
    (token: TokenInfo) => {
      console.log("选择代币2:", token);

      if (swapConfig?.token2) {
        // 取消订阅原来的 token2
        unsubscribe(swapConfig.token2?.address || "");
      }

      // 创建新的钱包数组，清空token2Balance
      const updatedWallets = walletConfigs.map((wallet) => ({
        ...wallet,
        token2Balance: undefined,
      }));

      // 更新钱包配置
      handleWalletConfigChange(updatedWallets);

      setToken2(token);
    },
    [setToken2, walletConfigs, handleWalletConfigChange]
  );

  // 处理代币交换
  const handleSwapTokens = useCallback(() => {
    if (!swapConfig || !swapConfig.token1 || !swapConfig.token2) return;

    const token1 = swapConfig.token1;
    const token2 = swapConfig.token2;

    dispatch(
      updateSwapConfig({
        ...swapConfig,
        token1: token2,
        token2: token1,
      })
    );

    console.log("交换前 - token1:", token1.symbol, token1.address);
    console.log("交换前 - token2:", token2.symbol, token2.address);
    console.log("交换后 - token1:", token2.symbol, token2.address);
    console.log("交换后 - token2:", token1.symbol, token1.address);
  }, [swapConfig, dispatch]);

  // 交易设置相关状态
  const [swapAmount, setSwapAmount] = useState<string>("");
  const [sellAmount, setSellAmount] = useState<string>("");
  const [autoSell, setAutoSell] = useState<boolean>(false);
  const [batchTaskRunning, setBatchTaskRunning] = useState<boolean>(false);
  const batchTaskRunningRef = useRef<boolean>(false);

  useEffect(() => {
    batchTaskRunningRef.current = batchTaskRunning;
  }, [batchTaskRunning]);

  // 处理队列配置变更
  const handleQueueConfigChange = useCallback(
    (newQueueConfig: QueueConfig) => {
      if (!task) return;

      const updatedTaskConfig: TaskConfig = {
        ...task,
        queue: newQueueConfig,
      };

      setTask(updatedTaskConfig);
    },
    [task, setTask]
  );

  // 处理滑点变更
  const handleSlippageChange = useCallback(
    (value: number) => {
      if (!swapConfig) return;

      const newConfig = {
        ...swapConfig,
        slippage: value,
      };

      dispatch(updateSwapConfig(newConfig));

      // 同步更新SwapParams中的滑点
      updatePartialParams({
        slippage: value,
      });
    },
    [swapConfig, dispatch, updatePartialParams]
  );

  // 处理交易数量变更
  const handleSwapAmountChange = useCallback(
    (value: string) => {
      setSwapAmount(value);
      // 更新SwapParams中的amountIn
      updatePartialParams({
        amountIn: value,
      });
    },
    [updatePartialParams]
  );

  // 处理卖出数量变更
  const handleSellAmountChange = useCallback((value: string) => {
    setSellAmount(value);
  }, []);

  // 处理自动卖出开关变更
  const handleAutoSellChange = useCallback(
    (enabled: boolean) => {
      console.log("自动卖出开关变更:", enabled);
      // 使用重命名后的updateAutoSell函数
      updateAutoSell(enabled);

      // 如果开启自动卖出，初始化swap参数
      if (enabled && !swapParams.amountIn && swapAmount) {
        initSwapParamsFromTokens();
        updatePartialParams({
          amountIn: swapAmount,
        });
      }
    },
    [
      updateAutoSell,
      swapParams.amountIn,
      swapAmount,
      initSwapParamsFromTokens,
      updatePartialParams,
    ]
  );

  // 处理金额类型变更
  const handleAmountTypeChange = useCallback(
    (type: string) => {
      console.log("金额类型变更:", type);
      setAmountType(type as AmountType);
    },
    [setAmountType]
  );

  // 处理最小随机金额变更
  const handleMinAmountChange = useCallback(
    (value: string) => {
      console.log("最小随机金额变更:", value);
      setMinAmount(value);
    },
    [setMinAmount]
  );

  // 处理最大随机金额变更
  const handleMaxAmountChange = useCallback(
    (value: string) => {
      console.log("最大随机金额变更:", value);
      setMaxAmount(value);
    },
    [setMaxAmount]
  );

  // 处理固定金额变更
  const handleFixedAmountChange = useCallback(
    (value: string) => {
      console.log("固定金额变更:", value);
      setFixedAmount(value);
    },
    [setFixedAmount]
  );

  // 处理保留金额变更
  const handleReserveAmountChange = useCallback(
    (value: string) => {
      console.log("保留金额变更:", value);
      setReserveAmount(value);
    },
    [setReserveAmount]
  );

  // 处理卖出金额类型变更
  const handleSellAmountTypeChange = useCallback(
    (type: string) => {
      console.log("卖出金额类型变更:", type);
      setSellAmountType(type as AmountType);
    },
    [setSellAmountType]
  );

  // 处理卖出最小随机金额变更
  const handleSellMinAmountChange = useCallback(
    (value: string) => {
      console.log("卖出最小随机金额变更:", value);
      setSellMinAmount(value);
    },
    [setSellMinAmount]
  );

  // 处理卖出最大随机金额变更
  const handleSellMaxAmountChange = useCallback(
    (value: string) => {
      console.log("卖出最大随机金额变更:", value);
      setSellMaxAmount(value);
    },
    [setSellMaxAmount]
  );

  // 处理卖出固定金额变更
  const handleSellFixedAmountChange = useCallback(
    (value: string) => {
      console.log("卖出固定金额变更:", value);
      setSellFixedAmount(value);
    },
    [setSellFixedAmount]
  );

  // 处理卖出保留金额变更
  const handleSellReserveAmountChange = useCallback(
    (value: string) => {
      console.log("卖出保留金额变更:", value);
      setSellReserveAmount(value);
    },
    [setSellReserveAmount]
  );

  // 初始化交易参数
  const initializeTradeParams = useCallback(() => {
    if (swapConfig?.token1 && swapConfig?.token2) {
      // 初始化交易参数
      initSwapParamsFromTokens();

      // 如果已经设置了交易金额，同步到SwapParams
      if (swapAmount) {
        updatePartialParams({
          amountIn: swapAmount,
        });
      }
    }
  }, [swapConfig, swapAmount, initSwapParamsFromTokens, updatePartialParams]);

  // 在代币改变时重新初始化交易参数
  useEffect(() => {
    if (swapConfig?.token1 && swapConfig?.token2) {
      initializeTradeParams();
    }
  }, [
    swapConfig?.token1?.address,
    swapConfig?.token2?.address,
    initializeTradeParams,
  ]);

  // 处理文本输入控制（只允许数字和小数点）
  const handleNumberInputChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      setter: (value: string) => void
    ) => {
      const value = e.target.value;
      if (/^(\d*\.?\d*)$/.test(value) || value === "") {
        setter(value);
      }
    },
    []
  );

  // Worker服务相关状态
  const [workerStatus, setWorkerStatus] = useState<{
    totalWorkers: number;
    busyWorkers: number;
    queueLength: number;
  }>({
    totalWorkers: 0,
    busyWorkers: 0,
    queueLength: 0,
  });

  useEffect(() => {
    if (
      !isInitialized &&
      dexConfig &&
      dexConfig.wallets &&
      dexConfig.wallets.length > 0
    ) {
      console.log("初始化DexService", dexConfig);
      initialize(dexConfig);
    }
  }, [isInitialized, dexConfig, initialize]);

  // 使用DexWorker Hook
  const dexWorkerConfig = useMemo(() => {
    console.log("初始化钱包dexWorkerConfig:", wallets, rpc, dex);
    return {
      config: {
        wallets: wallets,
        rpc: rpc,
        dex: dex,
        maxThreads: task?.queue?.threads || 5,
      },
      onLog: (log: LogData) => {
        addLog(log);
      },
    };
  }, [wallets, rpc, dex, addLog, task?.queue?.threads]);

  const dexWorker = useDexWorker(dexWorkerConfig);

  // 执行批量任务
  const executeBatchTask = useCallback(async () => {
    if (!task || !swapConfig?.token1 || !swapConfig?.token2) return;

    try {
      // 确保Worker实例存在
      if ((await dexWorker.getWorkerCount()) === 0) {
        console.log("创建Worker实例...");
        await dexWorker.createWorker();
      }

      // 设置任务运行状态
      setBatchTaskRunning(true);
      batchTaskRunningRef.current = true;
      console.log("任务已启动，配置已锁定", task);

      // 获取选中的钱包索引
      const selectedIndices =
        selectedWalletIndices.length > 0
          ? selectedWalletIndices
          : walletConfigs.map((_, index) => index);

      console.log("选中的钱包索引:", selectedIndices);

      if (selectedIndices.length === 0) {
        console.log("未选择任何钱包，任务终止");
        setBatchTaskRunning(false);
        return;
      }

      // 根据金额类型计算交易金额
      const calculateAmountIn = async (
        walletIndex: number
      ): Promise<string> => {
        const wallet = walletConfigs[walletIndex];
        if (!wallet || !wallet.address) return "0";

        const token1 = swapConfig.token1!;
        const provider = new ethers.providers.JsonRpcProvider(rpc.url);

        // 检查是否为原生代币(BNB)
        const isBNB =
          token1.address === ethers.constants.AddressZero ||
          token1.address.toLowerCase() ===
            "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee" ||
          token1.address.toLowerCase() ===
            "0x0000000000000000000000000000000000000000";

        console.log(
          `计算钱包[${walletIndex}]交易金额, 代币: ${token1.symbol}, 地址: ${token1.address}, 是否BNB: ${isBNB}`
        );

        switch (swapConfig.amountType) {
          case AmountType.ALL: {
            // 全部金额：查询余额，并确保BNB保留足够的gas费
            if (isBNB) {
              // 如果是BNB，保留一点作为gas费
              try {
                const balance = await provider.getBalance(wallet.address);
                console.log(
                  `钱包[${walletIndex}] BNB余额: ${ethers.utils.formatEther(
                    balance
                  )} BNB`
                );

                // 保留一点BNB作为Gas费
                const gasReserve = ethers.utils.parseEther("0.005");
                return balance.gt(gasReserve)
                  ? ethers.utils.formatUnits(
                      balance.sub(gasReserve),
                      token1.decimals
                    )
                  : "0";
              } catch (error) {
                console.error(`查询钱包[${walletIndex}] BNB余额出错:`, error);
                return "0";
              }
            } else {
              // 如果是代币，查询代币余额
              try {
                const tokenContract = new ethers.Contract(
                  token1.address,
                  ["function balanceOf(address) view returns (uint256)"],
                  provider
                );
                const balance = await tokenContract.balanceOf(wallet.address);
                console.log(
                  `钱包[${walletIndex}] ${
                    token1.symbol
                  }余额: ${ethers.utils.formatUnits(balance, token1.decimals)}`
                );
                return ethers.utils.formatUnits(balance, token1.decimals);
              } catch (error) {
                console.error(
                  `查询钱包[${walletIndex}] ${token1.symbol}余额出错:`,
                  error
                );
                return "0";
              }
            }
          }

          case AmountType.RANDOM: {
            // 随机金额：在最小和最大值之间随机
            if (!swapConfig.minAmount || !swapConfig.maxAmount) return "0";

            const min = parseFloat(swapConfig.minAmount);
            const max = parseFloat(swapConfig.maxAmount);
            const randomAmount = min + Math.random() * (max - min);
            return randomAmount.toString();
          }

          case AmountType.RANDOM_PERCENT: {
            // 随机百分比：查询余额后按百分比计算
            if (!swapConfig.minAmount || !swapConfig.maxAmount) return "0";

            const min = parseFloat(swapConfig.minAmount) / 100;
            const max = parseFloat(swapConfig.maxAmount) / 100;
            const randomPercent = min + Math.random() * (max - min);

            // 查询余额
            let balance: ethers.BigNumber;
            try {
              if (isBNB) {
                balance = await provider.getBalance(wallet.address);
                // 保留一点BNB作为Gas费
                const gasReserve = ethers.utils.parseEther("0.005");
                balance = balance.gt(gasReserve)
                  ? balance.sub(gasReserve)
                  : ethers.BigNumber.from(0);
              } else {
                const tokenContract = new ethers.Contract(
                  token1.address,
                  ["function balanceOf(address) view returns (uint256)"],
                  provider
                );
                balance = await tokenContract.balanceOf(wallet.address);
              }

              const amountBN = balance
                .mul(Math.floor(randomPercent * 10000))
                .div(10000);
              return ethers.utils.formatUnits(amountBN, token1.decimals);
            } catch (error) {
              console.error(
                `随机百分比计算钱包[${walletIndex}]余额出错:`,
                error
              );
              return "0";
            }
          }

          case AmountType.FIXED: {
            // 固定金额：直接使用设置的固定金额
            return swapConfig.fixedAmount || "0";
          }

          case AmountType.RESERVE: {
            // 保留固定金额：查询余额后减去保留金额
            if (!swapConfig.reserveAmount) return "0";

            const reserveAmount = parseFloat(swapConfig.reserveAmount);

            // 查询余额
            let balance: ethers.BigNumber;
            let formattedBalance: string;

            try {
              if (isBNB) {
                balance = await provider.getBalance(wallet.address);
                formattedBalance = ethers.utils.formatUnits(
                  balance,
                  token1.decimals
                );
                console.log(
                  `钱包[${walletIndex}] BNB余额: ${formattedBalance}`
                );
              } else {
                const tokenContract = new ethers.Contract(
                  token1.address,
                  ["function balanceOf(address) view returns (uint256)"],
                  provider
                );
                balance = await tokenContract.balanceOf(wallet.address);
                formattedBalance = ethers.utils.formatUnits(
                  balance,
                  token1.decimals
                );
                console.log(
                  `钱包[${walletIndex}] ${token1.symbol}余额: ${formattedBalance}`
                );
              }

              const availableAmount =
                parseFloat(formattedBalance) - reserveAmount;
              return availableAmount > 0 ? availableAmount.toString() : "0";
            } catch (error) {
              console.error(`保留金额计算钱包[${walletIndex}]余额出错:`, error);
              return "0";
            }
          }

          default:
            return swapParams.amountIn || "0";
        }
      };

      // 创建基础交易参数
      const baseParams: SwapParams = {
        tokenIn: swapConfig.token1.address,
        tokenOut: swapConfig.token2.address,
        amountIn: "0", // 将在每个钱包执行时动态计算
        slippage: swapConfig.slippage,
        tokenInDecimals: swapConfig.token1.decimals,
        tokenOutDecimals: swapConfig.token2.decimals,
      };

      // 设置相关钱包状态为等待中
      const walletIndices = selectedIndices.filter(index => index !== undefined) as number[];
      if (walletIndices.length > 0) {
        const pendingWalletStatus = { ...walletQueryStatus };
        walletIndices.forEach(index => {
          if (index >= 0 && index < walletConfigs.length) {
            const wallet = walletConfigs[index];
            if (wallet.address && pendingWalletStatus[wallet.address]) {
              pendingWalletStatus[wallet.address] = {
                ...pendingWalletStatus[wallet.address],
                status: "pending"
              };
            }
          }
        });
        setWalletQueryStatus(pendingWalletStatus);
      }

      // 设置Worker配置
      await dexWorker.setMaxWorkers(task.queue.threads || 3);
      console.log("Worker配置:", dexWorker.getWorkerCount());

      // 创建任务执行计数器
      let executedCount = 0;
      const maxExecutions = task.queue.taskCount;

      // 记录初始价格，用于价格变化停止条件
      let initialPrice: string | null = null;

      // 检查是否满足价格停止条件
      const checkPriceStopCondition = async (): Promise<boolean> => {
        // 如果不是价格相关的停止条件，直接返回false
        if (
          task.queue.stopCondition !== QueueStopCondition.PRICE_INCREASE &&
          task.queue.stopCondition !== QueueStopCondition.PRICE_DECREASE
        ) {
          return false;
        }

        try {
          // 确保token信息存在
          if (!swapConfig.token1 || !swapConfig.token2) {
            console.error("检查价格停止条件失败: token信息不完整");
            return false;
          }

          // 获取当前价格
          const params: SwapParams = {
            tokenIn: swapConfig.token1.address,
            tokenOut: swapConfig.token2.address,
            amountIn: "1000000000000000000", // 1单位的输入代币
            tokenInDecimals: swapConfig.token1.decimals,
            tokenOutDecimals: swapConfig.token2.decimals,
            slippage: swapConfig.slippage,
          };

          // getQuote直接返回字符串形式的报价
          const currentPrice = await dexWorker.getQuote(params);

          console.log(
            `当前价格: ${currentPrice}, 初始价格: ${initialPrice || "未设置"}`
          );

          // 如果初始价格未设置，设置初始价格并返回false
          if (!initialPrice) {
            initialPrice = currentPrice;
            return false;
          }

          // 检查价格变化
          const initialPriceValue = parseFloat(initialPrice);
          const currentPriceValue = parseFloat(currentPrice);

          if (task.queue.stopCondition === QueueStopCondition.PRICE_INCREASE) {
            // 检查价格是否上涨
            if (currentPriceValue > initialPriceValue) {
              console.log(
                `价格上涨停止条件满足: ${initialPriceValue} -> ${currentPriceValue}`
              );
              return true;
            }
          } else if (
            task.queue.stopCondition === QueueStopCondition.PRICE_DECREASE
          ) {
            // 检查价格是否下跌
            if (currentPriceValue < initialPriceValue) {
              console.log(
                `价格下跌停止条件满足: ${initialPriceValue} -> ${currentPriceValue}`
              );
              return true;
            }
          }

          return false;
        } catch (error) {
          console.error("检查价格停止条件出错:", error);
          return false;
        }
      };

      // 创建任务执行函数
      const executeTask = async () => {
        // 检查Worker实例
        if (dexWorker.getWorkerCount() === 0) {
          console.log("创建Worker实例...");
          await dexWorker.createWorker();
        }

        executedCount++;
        console.log(`开始执行第 ${executedCount}/${maxExecutions} 次任务`);

        // 处理钱包顺序
        let walletOrder = [...selectedIndices];

        // 执行模式处理
        if (task.queue.runMode === QueueRunMode.SERIAL) {
          console.log("串行模式执行");
        } else {
          console.log("并行模式执行");
        }

        let paramsArr = [];

        // 统一使用同一套逻辑处理
        for (const walletIndex of selectedIndices) {
          // 只有在第一次执行后才检查任务运行状态
          if (executedCount > 1 && !batchTaskRunningRef.current) {
            console.log("任务已停止，中断执行");
            break;
          }

          console.log(`钱包[${walletIndex}] 开始计算交易金额`);
          // 计算交易金额
          const amountIn = await calculateAmountIn(walletIndex);
          if (parseFloat(amountIn) <= 0) {
            console.log(`钱包[${walletIndex}] 可用金额为0，跳过执行`);
            continue;
          }

          // 创建交易参数
          const params = { 
            ...baseParams, 
            amountIn,
            walletIndex // 添加钱包索引，确保DexService使用正确的钱包
          };
          console.log(
            `钱包[${walletIndex}] 开始交易，金额: ${amountIn}`,
            params
          );

          paramsArr.push(params);
        }

        // 执行交易
        const results = await dexWorker.batchSwap(paramsArr);

        console.log("交易结果:", results);
        // 处理结果，batchSwap返回的是数组
        if (Array.isArray(results) && results.length > 0) {
          // 解析嵌套的交易结果数据结构
          // 交易结果数据结构为: [{workerId: number, success: boolean, data: [walletResults]}]
          const workerResponses = results.filter(r => r.success && Array.isArray(r.data));
          
          // 更新钱包状态
          const updatedWalletStatus = { ...walletQueryStatus };
          
          // 处理所有成功的工作结果
          for (const response of workerResponses) {
            if (Array.isArray(response.data)) {
              for (const walletResult of response.data) {
                if (typeof walletResult.walletIndex === 'number' && walletResult.walletIndex >= 0) {
                  const walletIndex = walletResult.walletIndex;
                  if (walletIndex < walletConfigs.length) {
                    const wallet = walletConfigs[walletIndex];
                    if (wallet.address && updatedWalletStatus[wallet.address]) {
                      // 更新钱包状态
                      updatedWalletStatus[wallet.address] = {
                        ...updatedWalletStatus[wallet.address],
                        status: walletResult.success ? "success" : "error",
                        errorMessage: walletResult.success ? undefined : 
                          (walletResult.result?.error || "交易失败")
                      };
                      
                      // 记录日志
                      if (walletResult.success) {
                        console.log(
                          `钱包[${walletIndex}] ${wallet.address} 交易成功: ${
                            walletResult.result?.txHash || ""
                          }`
                        );
                        // 记录交易成功日志
                        const successLog: CreateLogParams = {
                          level: LogLevel.SUCCESS,
                          message: `钱包[${walletIndex}] 交易成功`,
                          data: walletResult.result,
                        };
                        addLog(successLog);
                      } else {
                        console.log(
                          `钱包[${walletIndex}] ${wallet.address} 交易失败: ${
                            walletResult.result?.error || "未知错误"
                          }`
                        );
                        // 记录交易失败日志
                        const errorLog: CreateLogParams = {
                          level: LogLevel.ERROR,
                          message: `钱包[${walletIndex}] 交易失败`,
                          data: {
                            error: walletResult.result?.error || "未知错误",
                            result: walletResult.result
                          },
                        };
                        addLog(errorLog);
                      }
                    }
                  }
                }
              }
            }
          }
          
          // 如果没有找到有效的响应，记录错误
          if (workerResponses.length === 0) {
            console.log("未找到有效的工作响应");
            const errorLog: CreateLogParams = {
              level: LogLevel.ERROR,
              message: "未找到有效的工作响应",
              data: results,
            };
            addLog(errorLog);
          }
          
          // 更新钱包状态
          setWalletQueryStatus(updatedWalletStatus);
        }

        console.log("执行下一次任务判断", task.queue.stopCondition);

        // 检查价格停止条件
        if (
          task.queue.stopCondition === QueueStopCondition.PRICE_INCREASE ||
          task.queue.stopCondition === QueueStopCondition.PRICE_DECREASE
        ) {
          if (await checkPriceStopCondition()) {
            console.log("价格停止条件满足，任务停止");
            setBatchTaskRunning(false);
            batchTaskRunningRef.current = false;
            return false;
          }
        }

        // 检查任务状态 - 运行次数
        if (
          executedCount >= task.queue.stopValue &&
          task.queue.stopCondition === QueueStopCondition.RUN_COUNT
        ) {
          console.log(`已达到设定的执行次数 ${task.queue.stopValue}，任务停止`);
          setBatchTaskRunning(false);
          batchTaskRunningRef.current = false;
          return false;
        }

        // 处理循环执行 - 任务次数
        if (
          executedCount < maxExecutions &&
          task.queue.stopCondition === QueueStopCondition.RUN_COUNT
        ) {
          console.log(`等待 ${task.queue.interval} 秒后执行下一轮任务`);
          // 确保任务状态为运行中
          if (!batchTaskRunningRef.current) {
            console.log("任务已停止，不再执行下一轮");
            return false;
          }
          setTimeout(() => executeTask(), task.queue.interval * 1000);
          return true;
        } else if (
          task.queue.stopCondition === QueueStopCondition.PRICE_INCREASE ||
          task.queue.stopCondition === QueueStopCondition.PRICE_DECREASE
        ) {
          // 价格停止条件在每次执行前检查
          console.log(
            `等待 ${task.queue.interval} 秒后执行下一轮任务并检查价格条件`
          );
          // 确保任务状态为运行中
          if (!batchTaskRunningRef.current) {
            console.log("任务已停止，不再执行下一轮");
            return false;
          }
          setTimeout(() => executeTask(), task.queue.interval * 1000);
          return true;
        } else {
          // 任务完成
          console.log("任务已完成，设置状态为停止");
          // 使用函数形式的setState确保获取最新状态
          setBatchTaskRunning(false);
          // 确保ref也同步更新
          batchTaskRunningRef.current = false;
          return true;
        }
      };

      // 开始执行任务
      executeTask();
    } catch (error) {
      console.error("批量任务执行错误:", error);
      setBatchTaskRunning(false);
    }
  }, [
    task,
    swapConfig,
    swapParams,
    batchTaskRunningRef,
    dexWorker,
    selectedWalletIndices,
    walletConfigs,
    rpc.url,
    setBatchTaskRunning,
  ]);

  // 停止批量任务
  const stopBatchTask = useCallback(() => {
    if (!batchTaskRunningRef.current) return;

    console.log("正在停止任务...");

    // 直接更新ref和state
    batchTaskRunningRef.current = false;
    setBatchTaskRunning(false);

    // 终止Worker实例
    dexWorker.terminate();

    // 重新创建Worker
    setTimeout(async () => {
      await dexWorker.createWorker();
      console.log("任务已停止，Worker已重置");
    }, 1000);
  }, [dexWorker]);

  // 更新Worker状态的定时器
  useEffect(() => {
    if (!batchTaskRunningRef.current) return;

    const timer = setInterval(() => {
      setWorkerStatus({
        totalWorkers: dexWorker.getWorkerCount(),
        busyWorkers: dexWorker.getBusyWorkerCount(),
        queueLength: dexWorker.getActiveWorkerCount(),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [batchTaskRunningRef, dexWorker]);

  return (
    <div className="bsc-swap-container">
      {/* 测试区域 */}
      <div>
        {/* <button
          onClick={() => {
            console.log("dexConfig:", dexConfig);
            console.log("钱包配置:", wallets);
            console.log("RPC配置:", rpc);
            console.log("DEX配置:", dex);
            console.log("任务配置:", task);
            console.log("交换配置:", swapConfig);
            console.log("队列配置:", queueConfig);
            console.log("其他配置:", otherConfig);
            console.log("代币列表:", tokens);
            console.log("交易参数:", swapParams);
            console.log("gas设置:", gasSettings);
          }}
        >
          读取全局配置
        </button> */}
      </div>
      <div className="bsc-swap-content">
        {/* 钱包管理器部分 */}
        <div
          className={`wallet-manager-section ${
            showWalletManager ? "show" : "hide"
          }`}
        >
          <WalletManagerMUI
            wallets={combinedWalletConfigs}
            selectedWalletIndices={selectedWalletIndices}
            token1Symbol={swapConfig?.token1?.symbol || "Token1"}
            token2Symbol={swapConfig?.token2?.symbol || "Token2"}
            onImport={handleMUIWalletImport}
            onDelete={handleMUIWalletDelete}
            onCheckBalance={handleMUICheckBalance}
            onSelectWallets={handleMUIWalletSelect}
            onClearAll={handleMUIClearAll}
          />
        </div>

        {/* DEX选择器和网络选择器部分 - 并排布局 */}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            gap: "16px",
            padding: "16px",
            borderRadius: "6px",
            margin: "20px 0px",
            alignItems: "center",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {/* 网络选择器部分 */}
          <Box sx={{ flex: 1 }}>
            <NetworkSelector
              selectedRpc={rpc}
              onNetworkChange={handleNetworkChange}
            />
          </Box>
          {/* DEX选择器部分 */}
          <Box sx={{ flex: 1 }}>
            <DexSelector selectedDex={dex} onDexChange={handleDexChange} />
          </Box>
        </Paper>

        {/* 代币选择部分 */}
        <Paper
          elevation={0}
          sx={{
            padding: 1.5,
            borderRadius: 2,
            marginBottom: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              marginBottom: 1,
              fontWeight: 500,
              fontSize: "1rem",
              paddingLeft: 0.5,
            }}
          >
            代币选择
          </Typography>
          <TokenPair
            token1={swapConfig?.token1 || null}
            token2={swapConfig?.token2 || null}
            onToken1Change={handleToken1Change}
            onToken2Change={handleToken2Change}
            onSwapTokens={handleSwapTokens}
          />
        </Paper>

        {/* 价格监控部分 */}
        {swapConfig?.token1 && swapConfig?.token2 ? (
          <BscPriceMonitor />
        ) : (
          <Alert severity="info" sx={{ marginTop: 1 }}>
            请先选择交易代币对，然后价格监控将自动启用
          </Alert>
        )}

        {/* 交易设置部分 */}
        <SwapSettings
          token1={swapConfig?.token1 || null}
          token2={swapConfig?.token2 || null}
          swapAmount={swapAmount}
          onSwapAmountChange={handleSwapAmountChange}
          sellAmount={sellAmount}
          onSellAmountChange={handleSellAmountChange}
          slippage={swapConfig?.slippage || 0.5}
          onSlippageChange={handleSlippageChange}
          autoSell={swapConfig?.autoSell || false}
          onAutoSellChange={handleAutoSellChange}
          disabled={!swapConfig?.token1}
          token1Balance="100" // 后续可从钱包余额获取真实值
          token2Balance="100" // 后续可从钱包余额获取真实值
          amountType={swapConfig?.amountType || "ALL"}
          onAmountTypeChange={handleAmountTypeChange}
          minAmount={swapConfig?.minAmount || ""}
          onMinAmountChange={handleMinAmountChange}
          maxAmount={swapConfig?.maxAmount || ""}
          onMaxAmountChange={handleMaxAmountChange}
          fixedAmount={swapConfig?.fixedAmount || ""}
          onFixedAmountChange={handleFixedAmountChange}
          reserveAmount={swapConfig?.reserveAmount || ""}
          onReserveAmountChange={handleReserveAmountChange}
          sellAmountType={swapConfig?.sellAmountType || "ALL"}
          onSellAmountTypeChange={handleSellAmountTypeChange}
          sellMinAmount={swapConfig?.sellMinAmount || ""}
          onSellMinAmountChange={handleSellMinAmountChange}
          sellMaxAmount={swapConfig?.sellMaxAmount || ""}
          onSellMaxAmountChange={handleSellMaxAmountChange}
          sellFixedAmount={swapConfig?.sellFixedAmount || ""}
          onSellFixedAmountChange={handleSellFixedAmountChange}
          sellReserveAmount={swapConfig?.sellReserveAmount || ""}
          onSellReserveAmountChange={handleSellReserveAmountChange}
        />

        {/* 批量任务设置部分 */}
        {task && (
          <BatchTaskSettings
            queueConfig={task.queue}
            onQueueConfigChange={handleQueueConfigChange}
            disabled={!swapConfig?.token1 || batchTaskRunningRef.current}
            taskRunning={batchTaskRunningRef.current}
          />
        )}

        {/* GAS设置部分 */}
        <GasSettings
          disabled={!swapConfig?.token1 || batchTaskRunningRef.current}
        />

        {/* 任务控制按钮 */}
        <Paper
          elevation={3}
          sx={{
            padding: 3,
            marginTop: 2,
            marginBottom: 2,
            borderRadius: 2,
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(to right, #0d253f, #1a3a5f)"
                : "linear-gradient(to right, #e0eafc, #cfdef3)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* 背景装饰元素 */}
          <Box
            sx={{
              position: "absolute",
              top: -20,
              right: -20,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background: (theme) =>
                batchTaskRunningRef.current
                  ? theme.palette.mode === "dark"
                    ? "rgba(90, 141, 232, 0.1)"
                    : "rgba(39, 101, 222, 0.06)"
                  : theme.palette.mode === "dark"
                  ? "rgba(230, 237, 243, 0.05)"
                  : "rgba(39, 101, 222, 0.02)",
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -30,
              left: -30,
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: (theme) =>
                batchTaskRunningRef.current
                  ? theme.palette.mode === "dark"
                    ? "rgba(90, 141, 232, 0.06)"
                    : "rgba(39, 101, 222, 0.05)"
                  : theme.palette.mode === "dark"
                  ? "rgba(230, 237, 243, 0.1)"
                  : "rgba(39, 101, 222, 0.1)",
              zIndex: 0,
            }}
          />

          {/* 内容部分 */}
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: "bold",
                color: (theme) =>
                  batchTaskRunningRef.current
                    ? theme.palette.primary.light
                    : theme.palette.text.secondary,
                textAlign: "center",
                marginBottom: 1.5,
              }}
            >
              任务控制中心
            </Typography>
            <Divider sx={{ marginBottom: 3 }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                justifyContent: "space-between",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              {/* 左侧：操作按钮 */}
              <Box
                sx={{
                  flex: "1 1 300px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 0,
                }}
              >
                {!batchTaskRunningRef.current ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={!swapConfig?.token1 || !swapConfig?.token2}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      maxWidth: 280,
                      padding: "12px",
                      borderRadius: "50px",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 8px 16px 0 rgba(56, 116, 224, 0.3)"
                          : "0 8px 16px 0 rgba(39, 101, 222, 0.25)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "0 12px 20px 0 rgba(56, 116, 224, 0.4)"
                            : "0 12px 20px 0 rgba(39, 101, 222, 0.35)",
                      },
                    }}
                    onClick={() => {
                      if (task) {
                        // 执行批量任务
                        executeBatchTask();
                      }
                    }}
                  >
                    开始执行任务
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    fullWidth
                    startIcon={<StopIcon />}
                    sx={{
                      maxWidth: 280,
                      padding: "12px",
                      borderRadius: "50px",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 8px 16px 0 rgba(244, 67, 54, 0.3)"
                          : "0 8px 16px 0 rgba(211, 47, 47, 0.25)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "0 12px 20px 0 rgba(244, 67, 54, 0.4)"
                            : "0 12px 20px 0 rgba(211, 47, 47, 0.35)",
                      },
                    }}
                    onClick={() => {
                      // 设置任务停止状态
                      stopBatchTask();
                    }}
                  >
                    停止执行任务
                  </Button>
                )}

                {/* 状态提示 */}
                {batchTaskRunningRef.current ? (
                  <Alert
                    icon={
                      <SyncIcon sx={{ animation: "spin 2s linear infinite" }} />
                    }
                    severity="info"
                    variant="filled"
                    sx={{
                      width: "100%",
                      maxWidth: 280,
                      marginTop: 2,
                      borderRadius: "10px",
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 4px 12px 0 rgba(0, 0, 0, 0.3)"
                          : "0 4px 12px 0 rgba(0, 0, 0, 0.15)",
                      animation: "pulse 2s infinite",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                      任务执行中，配置暂时锁定...
                    </Typography>
                  </Alert>
                ) : (
                  <Alert
                    severity="success"
                    variant="outlined"
                    sx={{
                      width: "100%",
                      maxWidth: 280,
                      marginTop: 2,
                      borderRadius: "10px",
                      border: "1px solid",
                      borderColor: (theme) => theme.palette.success.main,
                    }}
                  >
                    <Typography variant="body2">
                      任务就绪，可以开始执行
                    </Typography>
                  </Alert>
                )}
              </Box>

              {/* 右侧：任务信息与进度 */}
              <Box
                sx={{
                  flex: "1 1 300px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    width: "100%",
                    padding: 2,
                    borderRadius: "10px",
                    backgroundColor: (theme) =>
                      batchTaskRunningRef.current
                        ? theme.palette.mode === "dark"
                          ? "rgba(90, 141, 232, 0.12)"
                          : "rgba(39, 101, 222, 0.06)"
                        : theme.palette.mode === "dark"
                        ? "rgba(230, 237, 243, 0.05)"
                        : "rgba(39, 101, 222, 0.02)",
                    border: "1px solid",
                    borderColor: (theme) =>
                      batchTaskRunningRef.current
                        ? theme.palette.mode === "dark"
                          ? "rgba(90, 141, 232, 0.4)"
                          : "rgba(39, 101, 222, 0.25)"
                        : theme.palette.mode === "dark"
                        ? "rgba(230, 237, 243, 0.1)"
                        : "rgba(39, 101, 222, 0.1)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: (theme) =>
                        batchTaskRunningRef.current
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                      fontWeight: "500",
                      textAlign: "center",
                      marginBottom: 1.5,
                    }}
                  >
                    任务配置详情
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                      justifyContent: "space-around",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        线程数量
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ color: (theme) => theme.palette.primary.main }}
                      >
                        {task?.queue.threads || "-"}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        任务数量
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ color: (theme) => theme.palette.primary.main }}
                      >
                        {task?.queue.taskCount || "-"}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        执行间隔
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ color: (theme) => theme.palette.primary.main }}
                      >
                        {task?.queue.interval || "-"}秒
                      </Typography>
                    </Box>
                  </Box>

                  {batchTaskRunningRef.current && (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          textAlign: "center",
                          mb: 1,
                        }}
                      >
                        任务执行进度
                      </Typography>
                      <LinearProgress
                        variant="indeterminate"
                        sx={{
                          height: 8,
                          borderRadius: 5,
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 1.5,
                          px: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: (theme) => theme.palette.primary.main }}
                        >
                          活跃线程: {workerStatus.busyWorkers}/
                          {workerStatus.totalWorkers}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: (theme) => theme.palette.primary.main }}
                        >
                          等待任务: {workerStatus.queueLength}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Paper>
              </Box>
            </Box>
          </Box>
        </Paper>
      </div>
    </div>
  );
};

export default BscSwap;
